"use client";

import React from "react";

/**
 * TipSelection — optional tip chips + custom amount, styled with the
 * booking-flow design tokens (chips mirror the design's TimeChip).
 */

interface TipSelectionProps {
  baseAmount: number;
  selectedTip: number;
  onTipChange: (amount: number) => void;
  className?: string;
}

import { FLOW_SPRING } from "@/components/booking-flow/themes";

export function TipSelection({ baseAmount, selectedTip, onTipChange, className }: TipSelectionProps) {
  const [customTip, setCustomTip] = React.useState<string>("");
  const [customFocus, setCustomFocus] = React.useState(false);

  // Calculate percentage-based tip suggestions
  const tipOptions = [
    { percent: 15, amount: Math.round(baseAmount * 0.15 * 100) / 100 },
    { percent: 20, amount: Math.round(baseAmount * 0.20 * 100) / 100 },
    { percent: 25, amount: Math.round(baseAmount * 0.25 * 100) / 100 }
  ];

  const handleCustomTipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomTip(value);

    // Convert to number and update the selected tip if it's a valid number
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onTipChange(numValue);
    } else {
      onTipChange(0);
    }
  };

  const handleTipSelect = (amount: number) => {
    // If the same tip amount is clicked again, deselect it by setting to 0
    if (selectedTip === amount) {
      onTipChange(0);
      setCustomTip("");
    } else {
      onTipChange(amount);
      setCustomTip("");
    }
  };

  return (
    <div className={className}>
      <div style={{
        fontSize: 10,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.22em',
        color: 'var(--f-ink-faint, rgba(28,18,12,0.40))',
        fontWeight: 600,
      }}>Add a tip (optional)</div>

      {/* Preset tip chips */}
      <div style={{
        marginTop: 12,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
      }}>
        {tipOptions.map((option) => {
          const selected = selectedTip === option.amount && customTip === "";
          return (
            <button
              key={option.percent}
              type="button"
              onClick={() => handleTipSelect(option.amount)}
              style={{
                height: 42,
                borderRadius: 11,
                border: `0.5px solid ${selected ? 'transparent' : 'var(--f-hairline, rgba(28,18,12,0.10))'}`,
                background: selected ? 'var(--f-sel-bg, #1c160f)' : 'var(--f-surface, #FBF8F2)',
                color: selected ? 'var(--f-sel-fg, #F5EDE0)' : 'var(--f-ink, #1c160f)',
                fontFamily: 'inherit',
                fontSize: 13,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.005em',
                cursor: 'pointer',
                transition: `background 240ms ${FLOW_SPRING}, color 240ms ${FLOW_SPRING}, border-color 200ms ease`,
                WebkitTapHighlightColor: 'transparent',
                boxShadow: selected ? '0 6px 14px rgba(28,18,12,0.18)' : 'none',
                padding: 0,
              }}
            >
              {option.percent}% · ${Number.isInteger(option.amount) ? option.amount : option.amount.toFixed(2)}
            </button>
          );
        })}
      </div>

      {/* Custom tip input */}
      <label style={{ display: 'block', position: 'relative', marginTop: 14 }}>
        <div style={{
          fontSize: 10,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.18em',
          fontWeight: 600,
          color: 'var(--f-ink-faint, rgba(28,18,12,0.40))',
          marginBottom: 6,
        }}>Custom amount</div>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 15,
            color: 'var(--f-ink-soft, rgba(28,18,12,0.62))',
          }}>$</span>
          <input
            id="customTip"
            value={customTip}
            onChange={handleCustomTipChange}
            onFocus={() => setCustomFocus(true)}
            onBlur={() => setCustomFocus(false)}
            placeholder="0"
            type="number"
            min="0"
            step="1"
            style={{
              width: '100%',
              padding: '10px 0 10px 14px',
              background: 'transparent',
              border: 'none',
              borderBottom: `1px solid ${customFocus ? 'var(--f-ink-soft, rgba(28,18,12,0.62))' : 'var(--f-hairline, rgba(28,18,12,0.10))'}`,
              fontFamily: 'inherit',
              fontSize: 15,
              color: 'var(--f-ink, #1c160f)',
              outline: 'none',
              letterSpacing: '-0.005em',
              transition: 'border-color 200ms ease',
            }}
          />
        </div>
      </label>
    </div>
  );
}
